import { MuiAlert, strIsNullOrEmpty } from '@fox/atoms';
import { DeviceType, useDeviceType } from '@fox/layout';
import React from 'react';
import { useContentContext } from '../../content-context';
import { useActionCentreColumns, useActionCentreSources } from '../query-hooks/query-implementations';
import { ActionCentreContextProvider } from './action-centre-context';
import { IActionCentreTableProps } from './contracts';
import { LeftSideFilterView } from './left-side-filter-view';
import { MobileView } from './mobile-view';
import { TopFilterView } from './top-filter-view';

export const ActionCentre: React.FC<IActionCentreTableProps> = ({
	actionId,
	rowsExpandedOnSmallScreens,
	disableExport,
	filterPositioning,
	itemsPerPage,
	displayMode
}) => {
	const pageSize = itemsPerPage || 10; // Default to 10 if not provided
	const { contentId, requestToken } = useContentContext();
	const deviceType = useDeviceType();

	const { data: sources, error, loading } = useActionCentreSources({ contentId, requestToken, actionId, skip: !actionId });
	const {
		data: columnsResult,
		error: columnsError,
		loading: columnsLoading
	} = useActionCentreColumns({
		actionId,
		contentId,
		requestToken,
		skip: !actionId
	});

	if (!strIsNullOrEmpty(error || columnsError)) return <MuiAlert severity="error">{error || columnsError}</MuiAlert>;
	if (loading || columnsLoading || !columnsResult?.columns?.length) return null;
	const sourceOptions = sources ? Object.entries(sources) : [];
	const showSearch = columnsResult.columns.some(c => c.filterType === 'string' && !!c.filterable);
	const showFilters = columnsResult.columns.some(c => c.filterable);

	// Default to "Table (default)" if no cardViewSettings are present
	const effectiveDisplayMode = columnsResult.cardViewSettings ? displayMode : 'Table (default)';
	return (
		<ActionCentreContextProvider sourceOptions={sourceOptions}>
			{deviceType === DeviceType.mobile ? (
				<MobileView
					actionId={actionId}
					columnsResult={columnsResult}
					disableExport={disableExport}
					showFilters={showFilters}
					pageSize={pageSize}
					sourceOptions={sourceOptions}
					showSearch={showSearch}
					rowsExpandedOnSmallScreens={rowsExpandedOnSmallScreens}
					displayMode={effectiveDisplayMode}
				/>
			) : filterPositioning === 'Left' && deviceType !== DeviceType.tablet ? (
				<LeftSideFilterView
					actionId={actionId}
					columnsResult={columnsResult}
					disableExport={disableExport}
					showFilters={showFilters}
					sourceOptions={sourceOptions}
					showSearch={showSearch}
					rowsExpandedOnSmallScreens={false}
					pageSize={pageSize}
					displayMode={effectiveDisplayMode}
				/>
			) : (
				<TopFilterView
					actionId={actionId}
					columnsResult={columnsResult}
					disableExport={disableExport}
					pageSize={pageSize}
					showFilters={showFilters}
					sourceOptions={sourceOptions}
					rowsExpandedOnSmallScreens={false}
					showSearch={showSearch}
					displayMode={effectiveDisplayMode}
				/>
			)}
		</ActionCentreContextProvider>
	);
};
